/**
 * Keep Fleet PO trucking (RentalPOTrackingSGM) in sync with Car Rental Invoicing POs.
 * - Creates missing PO master rows when a PO appears in CarRentalsSGM
 * - Upserts a CR-ADJ-{po} invoice so Total Amount Invoiced matches the car-rental sum
 */

import { ID, Query } from 'appwrite';
import {
  databases,
  DATABASE_ID,
  COLLECTION_ID_CAR_RENTALS,
  COLLECTION_ID_RENTAL_PO_TRACKING,
  COLLECTION_ID_RENTAL_INVOICE_TRACKING,
  COLLECTION_ID_RENTAL_COMPANIES
} from '../config/appwrite';

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

function normalizeCompany(name) {
  const t = String(name || '').trim();
  if (!t) return 'TBD';
  const n = t.toLowerCase().replace(/\s+/g, ' ');
  if (/^smiles?(\s+rent[\s-]*a[\s-]*car)?$/.test(n) || (n.includes('smile') && n.includes('rent'))) {
    return 'Smiles';
  }
  return t;
}

function normalizeProject(project) {
  const t = String(project || '').trim();
  if (!t) return 'TBD';
  const compact = t.toLowerCase().replace(/[()]/g, ' ').replace(/\s+/g, ' ').trim();
  if (compact === 'hep iib lot 10') return 'HEP IIB (Lot 10)';
  if (compact === 'hep iib lot 8') return 'HEP IIB (Lot 8)';
  return t;
}

function pickMostCommon(map) {
  let best = '';
  let count = -1;
  for (const [key, c] of map.entries()) {
    if (c > count) {
      best = key;
      count = c;
    }
  }
  return best;
}

function adjInvoiceNumber(poNumber) {
  return `CR-ADJ-${String(poNumber || '').trim()}`;
}

/** Ignore partial PO numbers created while typing in Car Rental (e.g. 66, 666, 66000). */
function isValidFleetPoNumber(poNumber) {
  const po = String(poNumber || '').trim();
  if (!po) return false;
  if (po.length < 9) return false;
  return /^\d{6,}$/.test(po);
}

function monthLabelFromCarRow(row) {
  const y = Number(row?.year);
  const m = Number(row?.month);
  if (!Number.isFinite(y) || !Number.isFinite(m) || m < 1 || m > 12) return '';
  return `${MONTHS[m - 1]} ${y}`;
}

function pickLatestCarRentalPeriod(carRows) {
  let best = null;
  carRows.forEach((row) => {
    const y = Number(row?.year) || 0;
    const m = Number(row?.month) || 0;
    const score = y * 12 + m;
    if (!best || score > best.score) {
      best = { year: y, month: m, score };
    }
  });
  return best;
}

async function listCarRentalsForPo(poNumber) {
  const po = String(poNumber || '').trim();
  if (!po) return [];
  const all = [];
  let cursor = null;
  for (let page = 0; page < 20; page += 1) {
    const queries = [Query.equal('poNumber', po), Query.limit(100)];
    if (cursor) queries.push(Query.cursorAfter(cursor));
    const res = await databases.listDocuments(DATABASE_ID, COLLECTION_ID_CAR_RENTALS, queries);
    all.push(...(res.documents || []));
    if ((res.documents || []).length < 100) break;
    cursor = res.documents[res.documents.length - 1].$id;
  }
  // Also catch case-variant PO numbers if stored differently
  if (!all.length) {
    const upper = po.toUpperCase();
    if (upper !== po) {
      return listCarRentalsForPo(upper);
    }
  }
  return all;
}

async function findPoDoc(poNumber) {
  const po = String(poNumber || '').trim();
  if (!po) return null;
  const res = await databases.listDocuments(DATABASE_ID, COLLECTION_ID_RENTAL_PO_TRACKING, [
    Query.equal('po_number', po),
    Query.limit(1)
  ]);
  return res.documents?.[0] || null;
}

async function findInvoiceByNumber(invoiceNumber) {
  const inv = String(invoiceNumber || '').trim();
  if (!inv) return null;
  const res = await databases.listDocuments(DATABASE_ID, COLLECTION_ID_RENTAL_INVOICE_TRACKING, [
    Query.equal('invoice_number', inv),
    Query.limit(1)
  ]);
  return res.documents?.[0] || null;
}

async function ensureRentalCompany(name) {
  const rentalName = normalizeCompany(name);
  if (!rentalName || rentalName === 'TBD') return;
  try {
    const existing = await databases.listDocuments(DATABASE_ID, COLLECTION_ID_RENTAL_COMPANIES, [
      Query.equal('rental_name', rentalName),
      Query.limit(1)
    ]);
    if (existing.documents?.length) return;
    await databases.createDocument(DATABASE_ID, COLLECTION_ID_RENTAL_COMPANIES, ID.unique(), {
      rental_name: rentalName,
      source: 'car-rental-sync'
    });
  } catch (e) {
    // Unique conflict or permissions — ignore; PO sync can still proceed.
    if (e?.code !== 409) console.warn('ensureRentalCompany:', e?.message || e);
  }
}

async function syncInvoicedAdjForPo(poNumber, supplier, project, carSum, carRows = []) {
  const po = String(poNumber || '').trim();
  if (!po) return;

  const invoices = await databases.listDocuments(DATABASE_ID, COLLECTION_ID_RENTAL_INVOICE_TRACKING, [
    Query.equal('po_number', po),
    Query.limit(500)
  ]);
  const adjNo = adjInvoiceNumber(po);
  let fleetExclAdj = 0;
  let adjDoc = null;
  for (const doc of invoices.documents || []) {
    const invNo = String(doc.invoice_number || '').trim();
    if (invNo === adjNo) {
      adjDoc = doc;
      continue;
    }
    fleetExclAdj += Number(doc.amount) || 0;
  }

  const diff = Math.round((Number(carSum) - fleetExclAdj) * 100) / 100;
  const period = pickLatestCarRentalPeriod(carRows);
  const now = new Date();
  const invoiceDate = period
    ? `${period.year}-${String(period.month).padStart(2, '0')}-01`
    : now.toISOString().slice(0, 10);
  const invoiceMonth = period
    ? monthLabelFromCarRow({ year: period.year, month: period.month })
    : `${MONTHS[now.getMonth()]} ${now.getFullYear()}`;

  if (diff <= 0.01) {
    if (adjDoc) {
      await databases.deleteDocument(
        DATABASE_ID,
        COLLECTION_ID_RENTAL_INVOICE_TRACKING,
        adjDoc.$id
      );
    }
    return;
  }

  const payload = {
    invoice_number: adjNo,
    po_number: po,
    supplier: normalizeCompany(supplier),
    project: normalizeProject(project),
    invoice_date: invoiceDate,
    invoice_month: invoiceMonth,
    amount: diff,
    vat_applies: true,
    status: 'Pending Signature',
    date_paid: '',
    remark: 'Auto-synced from Car Rental Invoicing (PO total)',
    pr_no: ''
  };

  if (adjDoc) {
    await databases.updateDocument(
      DATABASE_ID,
      COLLECTION_ID_RENTAL_INVOICE_TRACKING,
      adjDoc.$id,
      payload
    );
    return;
  }

  // Race-safe: another tab may have created it
  const existing = await findInvoiceByNumber(adjNo);
  if (existing) {
    await databases.updateDocument(
      DATABASE_ID,
      COLLECTION_ID_RENTAL_INVOICE_TRACKING,
      existing.$id,
      payload
    );
    return;
  }

  await databases.createDocument(
    DATABASE_ID,
    COLLECTION_ID_RENTAL_INVOICE_TRACKING,
    ID.unique(),
    payload
  );
}

/**
 * Ensure Fleet PO trucking has this PO and invoiced total matches Car Rental sum.
 */
export async function syncFleetPoFromCarRentalPo(poNumber) {
  const po = String(poNumber || '').trim();
  if (!po || !isValidFleetPoNumber(po)) return { created: false, poNumber: po, skipped: 'invalid-po' };

  const carRows = await listCarRentalsForPo(po);
  if (!carRows.length) return { created: false, poNumber: po, skipped: 'no-car-rows' };

  const companies = new Map();
  const projects = new Map();
  let carSum = 0;
  carRows.forEach((row) => {
    carSum += Number(row.amount) || 0;
    const company = normalizeCompany(row.rentalCompany);
    const project = normalizeProject(row.project);
    if (company && company !== 'TBD') companies.set(company, (companies.get(company) || 0) + 1);
    if (project && project !== 'TBD') projects.set(project, (projects.get(project) || 0) + 1);
  });

  const supplier = pickMostCommon(companies) || 'TBD';
  const project = pickMostCommon(projects) || 'TBD';

  await ensureRentalCompany(supplier);

  let created = false;
  let poDoc = await findPoDoc(po);
  if (!poDoc) {
    poDoc = await databases.createDocument(
      DATABASE_ID,
      COLLECTION_ID_RENTAL_PO_TRACKING,
      ID.unique(),
      {
        po_number: po,
        supplier,
        project,
        sd: 'TBD',
        total_po_amount: 0
      }
    );
    created = true;
  }

  await syncInvoicedAdjForPo(po, supplier, project, carSum, carRows);

  return {
    created,
    poNumber: po,
    supplier,
    project,
    carSum
  };
}

/**
 * Sync all distinct POs currently present in Car Rental Invoicing.
 */
export async function syncAllFleetPosFromCarRentals() {
  const poSet = new Set();
  let cursor = null;
  for (let page = 0; page < 50; page += 1) {
    const queries = [Query.limit(100)];
    if (cursor) queries.push(Query.cursorAfter(cursor));
    const res = await databases.listDocuments(DATABASE_ID, COLLECTION_ID_CAR_RENTALS, queries);
    for (const doc of res.documents || []) {
      const po = String(doc.poNumber || '').trim();
      if (po && isValidFleetPoNumber(po)) poSet.add(po);
    }
    if ((res.documents || []).length < 100) break;
    cursor = res.documents[res.documents.length - 1].$id;
  }

  const results = [];
  for (const po of [...poSet].sort()) {
    try {
      results.push(await syncFleetPoFromCarRentalPo(po));
    } catch (e) {
      console.error('syncFleetPoFromCarRentalPo failed:', po, e);
      results.push({ poNumber: po, error: e?.message || String(e) });
    }
  }
  return results;
}

/** Debounced per-PO sync so rapid cell edits don't spam Appwrite. */
const pendingPoSync = new Map();

export function scheduleFleetPoSyncFromCarRental(poNumber, delayMs = 900) {
  const po = String(poNumber || '').trim();
  if (!po) return;
  const existing = pendingPoSync.get(po);
  if (existing) clearTimeout(existing);
  const timer = setTimeout(async () => {
    pendingPoSync.delete(po);
    try {
      await syncFleetPoFromCarRentalPo(po);
    } catch (e) {
      console.error('scheduleFleetPoSyncFromCarRental:', po, e);
    }
  }, delayMs);
  pendingPoSync.set(po, timer);
}
